import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { A11yModule } from '@angular/cdk/a11y';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { RunnerPairingService, type PairedRunner } from '../../services/runner-pairing.service';

/**
 * Dialog „Rechner umbenennen“ auf /runners (NEX-1734). Speichert selbst und meldet den Rechner aus
 * der Serverantwort.
 *
 * **Ohne Design-Entwurf**: im Stil von `app-confirm-dialog`. Eigenes Markup, weil jener Dialog den
 * Fokus nicht fängt und eine Änderung dort alle Bestätigungen der App träfe.
 */
@Component({
  selector: 'app-runner-rename-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [A11yModule, MatButton, MatIcon, MatProgressSpinner, TranslatePipe],
  templateUrl: './runner-rename-dialog.component.html',
  styleUrl: './runner-rename-dialog.component.scss',
})
export class RunnerRenameDialogComponent {
  private readonly service = inject(RunnerPairingService);
  private readonly destroyRef = inject(DestroyRef);

  readonly runner = input.required<PairedRunner>();
  readonly renamed = output<PairedRunner>();
  readonly cancelled = output<void>();

  readonly name = linkedSignal(() => this.runner().name);
  readonly saving = signal(false);
  /** Übersetzungsschlüssel der Meldung am Feld. */
  readonly error = signal<string | null>(null);
  readonly canSave = computed(() => {
    const name = this.name().trim();
    return name !== '' && name !== this.runner().name && !this.saving();
  });

  private readonly nameInput = viewChild.required<ElementRef<HTMLInputElement>>('nameInput');

  constructor() {
    afterNextRender(() => this.nameInput().nativeElement.focus());
  }

  onInput(event: Event): void {
    this.name.set((event.target as HTMLInputElement).value);
    this.error.set(null);
  }

  save(): void {
    if (!this.canSave()) {
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    this.service
      .renameRunner(this.runner().id, this.name().trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.saving.set(false);
          this.renamed.emit(updated);
        },
        error: (err: unknown) => {
          this.saving.set(false);
          this.error.set(
            err instanceof HttpErrorResponse && err.error?.code === 'RUNNER_NAME_TAKEN'
              ? 'runners.rename.nameTaken'
              : 'runners.rename.failed',
          );
        },
      });
  }

  /** Während des Speicherns läuft die Änderung schon; Schließen verbärge ihr Ergebnis. */
  cancel(): void {
    if (!this.saving()) {
      this.cancelled.emit();
    }
  }

  onEnter(event: Event): void {
    event.preventDefault();
    this.save();
  }
}
